import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenues, createVenue } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Store } from "lucide-react";

// Şubeler listesi: yalnızca KART tıklama → şubeye gir. Düzenle/Sil
// butonları KART üzerinden kaldırıldı; hassas işlemler İşletme Ayarları
// içinde (`/venues/:id/settings`) — kart üzerinden yanlışlıkla tetiklenmesi
// riski yok. Yeni şube ekleme ise üst sağdaki "Yeni şube" butonunda.
export function VenuesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");

  const { data: venues, isLoading } = useQuery({
    queryKey: ["venues"],
    queryFn: getVenues,
  });

  const createMutation = useMutation({
    mutationFn: () => createVenue({ name, address: address || undefined }),
    onSuccess: (venue) => {
      qc.invalidateQueries({ queryKey: ["venues"] });
      setOpen(false);
      setName("");
      setAddress("");
      navigate(`/venues/${venue.id}/menu`);
    },
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("venues")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger
            render={<Button><Plus className="mr-2 size-4" />{t("new_venue")}</Button>}
          />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("create_new_venue")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(); }} className="space-y-4">
              <div>
                <Label>{t("venue_name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ornek: Cafe Istanbul" required />
              </div>
              <div>
                <Label>{t("address")}</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Kadikoy, Istanbul" />
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                {createMutation.isPending ? t("creating") : t("create_venue")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>
      ) : venues?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Store className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_venues")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {venues?.map((venue) => (
            <Card key={venue.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => navigate(`/venues/${venue.id}/menu`)}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-start justify-between mb-1 gap-2">
                  {/* line-clamp-3 ile uzun şube adı 3 satıra kadar wrap olur,
                      truncate'la 1 satıra sıkışıp anlamsız "Barracud..." çıkmıyor. */}
                  <h3 className="font-bold text-base leading-tight break-words [overflow-wrap:anywhere] line-clamp-3 flex-1 min-w-0">{venue.name}</h3>
                  <Badge variant={venue.planType === "PRO" ? "default" : "secondary"} className="shrink-0 mt-0.5">{venue.planType}</Badge>
                </div>
                {venue.address && <p className="text-xs text-muted-foreground">{venue.address}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
